import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { NavLink } from 'react-router-dom';
import HistoryIcon from '@mui/icons-material/History';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import VisibilityIcon from '@mui/icons-material/Visibility';
import HeaderResponsive from '../../../components/layouts/HeaderResponsive';
import Title from '../../../components/Title';
import EmptyStateMessage from '../../../components/layouts/EmptyStateMessage';
import SimpleCustomTable from '../../../components/table/SimpleCustomTable';
import {
  iconActionColumnSizing,
  simpleTableIconButtonSx,
} from '../../../components/table/iconActionColumnSizing';
import { SimpleColumn } from '../../../components/table/interface';
import PermissionButton from '../../../components/common/PermissionButton';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';
import { useDrawer } from '../../../components/context/DrawerContext';
import { stickyListRegionWidth } from '../../../components/layouts/StickyListToolbar';
import { Office } from '../../../models/office';
import { Collect } from '../../../models/collect';
interface ShowAlerts {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}

interface MobileSyncAuditListRow {
  id: number;
  created_at: string;
  event_type?: string | null;
  sync_entity?: string | null;
  status?: string | null;
  message?: string | null;
  user_id?: number | null;
  collect_id?: number | null;
  route_id?: number | null;
  lend_id?: number | null;
  local_ref?: string | null;
  app_version?: string | null;
  device_installation_id?: string | null;
  response_status?: number | null;
  office_name?: string | null;
  collect_name?: string | null;
  user_name?: string | null;
  username?: string | null;
}

interface MobileSyncAuditDetail extends MobileSyncAuditListRow {
  request_payload?: string | null;
  response_body?: string | null;
  meta_json?: string | null;
  office_id?: number | null;
}

const formatDateTime = (value?: string) => {
  if (!value) return '';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString('es-CO');
};

const prettifyMaybeJson = (raw?: string | null) => {
  if (raw === null || raw === undefined || raw === '') return '';
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch (_e) {
    return raw;
  }
};

const statusColorMap: Record<string, string> = {
  success: '#1b5e20',
  error: '#b71c1c',
  warning: '#b26a00',
  warn: '#b26a00',
  info: '#0d47a1',
  partial: '#6a1b9a',
};

const MobileSyncAuditLogs: React.FC<ShowAlerts> = ({
  showAlert,
  showLoading,
  hideLoading,
}) => {
  document.title = 'Auditoría sincronización mobile';

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { drawerOpen } = useDrawer();
  const { canRead } = useResourcePermissions('/procesos/auditoria-sincronizacion');

  const [rows, setRows] = useState<MobileSyncAuditListRow[]>([]);
  const [offices, setOffices] = useState<Office[]>([]);
  const [collects, setCollects] = useState<Collect[]>([]);
  const [officeId, setOfficeId] = useState('');
  const [collectId, setCollectId] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState<MobileSyncAuditDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const loadRows = async (officeFilter = officeId, collectFilter = collectId, status = statusFilter) => {
    if (!canRead) {
      setRows([]);
      return;
    }
    showLoading();
    try {
      const { data } = await axios.get('process/mobile-sync-audit', {
        params: {
          office_id: officeFilter || undefined,
          collect_id: collectFilter || undefined,
          status: status || undefined,
          limit: 350,
        },
      });

      setRows(data?.data ?? []);
    } catch (error: any) {
      console.error('Error cargando auditoría sync:', error);
      showAlert(
        error?.response?.data?.message || 'No fue posible cargar la auditoría de sincronización.',
        'error',
      );
    } finally {
      hideLoading();
    }
  };

  useEffect(() => {
    const bootstrap = async () => {
      showLoading();
      try {
        const { data } = await axios.get('offices');
        setOffices(data?.data ?? []);
      } catch (error) {
        showAlert('No fue posible cargar las oficinas.', 'error');
      } finally {
        hideLoading();
      }
    };

    bootstrap();
    if (canRead) {
      loadRows('', '', '');
    }
  }, [canRead]);

  useEffect(() => {
    if (!officeId) {
      setCollects([]);
      setCollectId('');
      return;
    }

    const fetchCollects = async () => {
      showLoading();
      try {
        const { data } = await axios.get(`collectsoffice/${officeId}`);
        setCollects(data ?? []);
      } catch (error) {
        setCollects([]);
        showAlert('No fue posible cargar los cobros de la oficina.', 'warning');
      } finally {
        hideLoading();
      }
    };

    fetchCollects();
  }, [officeId]);

  const handleSearch = async () => {
    if (!canRead) {
      showAlert('Sin permiso para consultar la auditoría.', 'warning');
      return;
    }
    await loadRows();
  };

  const openDetail = async (id: number) => {
    if (!canRead) return;
    setDetailLoading(true);
    setDetail(null);
    setDetailOpen(true);
    try {
      const { data } = await axios.get(`process/mobile-sync-audit/${id}`);
      setDetail(data?.data ?? null);
    } catch (error: any) {
      showAlert(error?.response?.data?.message || 'No fue posible cargar el detalle.', 'error');
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const columns: SimpleColumn[] = [
    { field: 'id', headerName: 'ID', minWidth: 90, align: 'center' },
    { field: 'created_label', headerName: 'Fecha', minWidth: 170 },
    {
      field: 'status',
      headerName: 'Estado',
      minWidth: 110,
      renderCell: ({ row }) => (
        <Typography
          variant="body2"
          sx={{
            fontWeight: 700,
            color: statusColorMap[String(row.status_key || '').toLowerCase()] || statusColorMap.info,
          }}
        >
          {row.status_label}
        </Typography>
      ),
    },
    { field: 'event_type', headerName: 'Evento', minWidth: 140 },
    { field: 'sync_entity', headerName: 'Entidad', minWidth: 120 },
    { field: 'office_name', headerName: 'Oficina', minWidth: 160, wrapText: true },
    { field: 'collect_name', headerName: 'Cobro', minWidth: 160, wrapText: true },
    { field: 'user_display', headerName: 'Usuario', minWidth: 180, wrapText: true },
    { field: 'route_id', headerName: 'Ruta', minWidth: 80, align: 'center' },
    { field: 'lend_id', headerName: 'Préstamo', minWidth: 90, align: 'center' },
    { field: 'local_ref', headerName: 'Ref. local', minWidth: 160, wrapText: true },
    { field: 'app_version', headerName: 'App', minWidth: 100 },
    { field: 'device_installation_id', headerName: 'Instalación', minWidth: 200, wrapText: true },
    { field: 'response_status', headerName: 'HTTP', minWidth: 70, align: 'center' },
    { field: 'message', headerName: 'Mensaje', minWidth: 360, wrapText: true },
    {
      field: 'opt_detail',
      headerName: '',
      ...iconActionColumnSizing(isMobile),
      renderCell: ({ row }) => (
        <Tooltip title="Ver payload / respuesta">
          <IconButton
            size="small"
            sx={simpleTableIconButtonSx}
            onClick={() => openDetail(Number(row.id))}
            disabled={!canRead}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  const tableRows = rows.map((r) => ({
    ...r,
    id: r.id,
    created_label: formatDateTime(r.created_at),
    status_key: (r.status || 'info').toLowerCase(),
    status_label: r.status || '—',
    user_display: [r.user_name?.trim(), r.username].filter(Boolean).join(' · ') || '—',
    route_id: r.route_id ?? '—',
    lend_id: r.lend_id ?? '—',
    response_status: r.response_status ?? '—',
  }));

  return (
    <HeaderResponsive>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        flexDirection={{ xs: 'column', sm: 'row' }}
        gap={1}
        mb={2}
      >
        <Title>Auditoría sincronización mobile</Title>
        <Box display="flex" gap={1} ml="auto" flexWrap="wrap">
          <Button variant="outlined" startIcon={<ArrowBackIcon />} component={NavLink} to="/dashboard">
            Regresar
          </Button>
        </Box>
      </Box>

      <Card>
        <CardContent
          sx={{
            width: (t) => stickyListRegionWidth(t, drawerOpen),
            maxWidth: '100%',
            boxSizing: 'border-box',
            overflowX: 'auto',
          }}
        >
          <Grid container spacing={2} mb={2}>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Oficina</InputLabel>
                <Select
                  value={officeId}
                  label="Oficina"
                  onChange={(event) => setOfficeId(String(event.target.value))}
                >
                  <MenuItem value="">Todas</MenuItem>
                  {offices.map((office) => (
                    <MenuItem key={office.id} value={office.id}>
                      {office.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small" disabled={!officeId}>
                <InputLabel>Cobro</InputLabel>
                <Select
                  value={collectId}
                  label="Cobro"
                  onChange={(event) => setCollectId(String(event.target.value))}
                >
                  <MenuItem value="">Todos</MenuItem>
                  {collects.map((collect) => (
                    <MenuItem key={collect.id} value={collect.id}>
                      {collect.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Estado</InputLabel>
                <Select
                  value={statusFilter}
                  label="Estado"
                  onChange={(event) => setStatusFilter(String(event.target.value))}
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="error">error</MenuItem>
                  <MenuItem value="success">success</MenuItem>
                  <MenuItem value="warning">warning</MenuItem>
                  <MenuItem value="info">info</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={12}>
              <PermissionButton
                allowed={canRead}
                deniedTooltip="Sin permiso para consultar"
                tooltip="Consultar"
                variant="contained"
                startIcon={<HistoryIcon />}
                onClick={handleSearch}
                sx={{ height: '40px', minWidth: 200 }}
              >
                Consultar
              </PermissionButton>
            </Grid>
          </Grid>

          {tableRows.length > 0 ? (
            <SimpleCustomTable
              rows={tableRows}
              columns={columns}
              maxHeight={560}
              exportCsv
              exportFileName="auditoria-sync-mobile.csv"
              columnGroups={[{ header: 'Opciones', fields: ['opt_detail'] }]}
              wrapCellContent
            />
          ) : (
            <EmptyStateMessage
              message="No hay registros de auditoría para los filtros seleccionados."
              align="left"
            />
          )}
        </CardContent>
      </Card>

      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Detalle auditoría #{detail?.id ?? ''}</DialogTitle>
        <DialogContent dividers>
          {detailLoading ? (
            <Typography>Cargando…</Typography>
          ) : detail ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="body2">
                <strong>Fecha:</strong> {formatDateTime(detail.created_at)}
              </Typography>
              <Typography variant="body2">
                <strong>Oficina / Cobro:</strong> {detail.office_name ?? '—'} /{' '}
                {detail.collect_name ?? '—'}
              </Typography>
              <Typography variant="body2">
                <strong>Usuario:</strong>{' '}
                {[detail.user_name?.trim(), detail.username].filter(Boolean).join(' · ') || '—'}
              </Typography>
              <Typography variant="body2">
                <strong>Mensaje:</strong> {detail.message || '—'}
              </Typography>
              <Typography variant="subtitle2">Request payload</Typography>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  p: 1,
                  bgcolor: 'action.hover',
                  borderRadius: 1,
                  maxHeight: 220,
                  overflow: 'auto',
                  fontSize: 12,
                }}
              >
                {prettifyMaybeJson(detail.request_payload) || '—'}
              </Box>
              <Typography variant="subtitle2">Response body</Typography>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  p: 1,
                  bgcolor: 'action.hover',
                  borderRadius: 1,
                  maxHeight: 220,
                  overflow: 'auto',
                  fontSize: 12,
                }}
              >
                {prettifyMaybeJson(detail.response_body) || '—'}
              </Box>
              <Typography variant="subtitle2">Meta</Typography>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  p: 1,
                  bgcolor: 'action.hover',
                  borderRadius: 1,
                  maxHeight: 160,
                  overflow: 'auto',
                  fontSize: 12,
                }}
              >
                {prettifyMaybeJson(detail.meta_json) || '—'}
              </Box>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </HeaderResponsive>
  );
};

export default MobileSyncAuditLogs;
